import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { bannersService } from '../../lib/api/shopService';
import { BANNER_SECTION, BANNER_TYPE } from '../../types';
import { getImageUrl } from '../../utils/apiHelpers';
import {
  getBannerId,
  isBannerVisible,
  normalizeBannersList,
} from '../../utils/bannerHelpers';

const SLIDE_INTERVAL_MS = 4000;

/** Image nette centrée + fond flou qui remplit la zone (bon rendu quel que soit le ratio). */
const BannerSlideMedia = ({ src, alt, eager = false }) => (
  <div className="relative w-full h-full overflow-hidden bg-gray-900">
    <img
      src={src}
      alt=""
      aria-hidden
      className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-2xl brightness-75 saturate-150"
      loading={eager ? undefined : 'lazy'}
      decoding="async"
      draggable={false}
    />
    <div className="absolute inset-0 bg-black/20" aria-hidden />
    <img
      src={src}
      alt={alt}
      className="relative z-10 h-full w-full object-contain object-center"
      loading={eager ? undefined : 'lazy'}
      decoding="async"
      draggable={false}
    />
  </div>
);

const Section2BannerCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const pausedRef = useRef(false);

  const { data: bannersData, isLoading } = useQuery({
    queryKey: ['banners', BANNER_TYPE.PROMO, BANNER_SECTION.SECTION_2],
    queryFn: () =>
      bannersService.getBanners({
        type: BANNER_TYPE.PROMO,
        section: BANNER_SECTION.SECTION_2,
        visibleOnly: true,
      }),
    enabled: true,
  });

  const banners = useMemo(() => {
    const list = normalizeBannersList(bannersData);
    return list.filter(
      (b) =>
        (!b.section || b.section === BANNER_SECTION.SECTION_2) &&
        isBannerVisible(b),
    );
  }, [bannersData]);

  const slideCount = banners.length;

  const goTo = useCallback(
    (index) => {
      if (slideCount === 0) return;
      setCurrentIndex(((index % slideCount) + slideCount) % slideCount);
    },
    [slideCount],
  );

  const next = useCallback(() => {
    setCurrentIndex((prev) => (slideCount <= 1 ? prev : (prev + 1) % slideCount));
  }, [slideCount]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) =>
      slideCount <= 1 ? prev : (prev - 1 + slideCount) % slideCount,
    );
  }, [slideCount]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [slideCount]);

  useEffect(() => {
    if (slideCount <= 1) return undefined;
    const id = setInterval(() => {
      if (!pausedRef.current) {
        setCurrentIndex((prev) => (prev + 1) % slideCount);
      }
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [slideCount]);

  if (isLoading) {
    return (
      <div className="w-full h-[60vh] bg-gray-200 animate-pulse" aria-hidden />
    );
  }

  if (slideCount === 0) {
    return null;
  }

  return (
    <section
      className="relative w-full h-[60vh] overflow-hidden bg-gray-900"
      aria-label="Bannières promotionnelles"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      {banners.map((banner, index) => {
        const active = index === currentIndex;
        const src = getImageUrl(banner.imagePath);
        const alt = banner.title || 'Bannière promo';
        const media = <BannerSlideMedia src={src} alt={alt} eager={active} />;

        return (
          <div
            key={getBannerId(banner) || index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              active ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            aria-hidden={!active}
          >
            {banner.link ? (
              <a
                href={banner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-full"
              >
                {media}
              </a>
            ) : (
              media
            )}
          </div>
        );
      })}

      {slideCount > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 text-white text-2xl leading-none hover:bg-black/60 transition"
            aria-label="Bannière précédente"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={next}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 text-white text-2xl leading-none hover:bg-black/60 transition"
            aria-label="Bannière suivante"
          >
            ›
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {banners.map((banner, index) => (
              <button
                key={getBannerId(banner) || `dot-${index}`}
                type="button"
                onClick={() => goTo(index)}
                className={`h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'w-6 bg-white'
                    : 'w-2.5 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Aller à la bannière ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default Section2BannerCarousel;
